import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { API_TOOLS_URL, AUTH_REDIRECT_URL } from '../constants';

type Token = {
  access_token: string;
  refresh_token: string;
  id_token: string;
  scope: string;
  token_type: string;
  expires_in: number;
  refresh_token_expires_in: number;
  active: boolean;
};

type TokenResponse = {
  reason: string;
  message: string;
  response: Token;
  status: string;
};

export type MyProfileResponse = {
  status: string;
  id: number;
  name: string;
  email: string;
  cover: string;
  roles: string[];
  created: string;
  username: string;
  tags: string[];
  tags_count: number;
  is_verified: boolean;
  is_activated: boolean;
  verified_type: string;
  photo: string;
  default_profile_pic: boolean;
  email_encoded: string;
  brand: {
    contacts: string[];
    packages: string[];
  };
  provider: string;
  firebase: {
    apple: string[];
    android: string[];
  };
  connections: string[];
  has_password: boolean;
  verified_emails: string[];
  username_changed: boolean;
  verified_categories: string[];
  likes_count: number;
  following_count: number;
  followers_count: number;
  teams: string[];
  no_index: boolean;
  photos_count: number;
  stickers_count: number;
  subscription: {
    is_trial_used: boolean;
    granted: boolean;
  };
  has_user_followings: boolean;
  key: string;
  registered: boolean;
  email_verification: {
    popup_data: {
      show_popup: boolean;
    };
  };
  type: string;
  points: number;
  saves_count: number;
  remix_score: number;
  top_fans_count: number;
  locations_count: number;
  has_top_fans: boolean;
  dashboard_visibility: boolean;
  interests: string[];
  permissions: string[];
  shop_subscriptions: string[];
  selected_interests: {
    sticker: string[];
    photo: string[];
  };
  mature: boolean;
  show_report_popup: boolean;
  is_verified_type_new: boolean;
};

export default class BaseAuth {
  private axios: AxiosInstance;
  private token: Token;
  private tokenExpireTime: number;
  private tokenExpireDelta: number;

  constructor(baseURL: string, tokenExpireDelta = 1) {
    this.axios = axios.create({
      baseURL,
      timeout: 10000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Platform: 'quicktools',
      },
    });
    this.tokenExpireDelta = tokenExpireDelta;
    this.setTokenInterceptors();
  }

  request<Res>(config: AxiosRequestConfig<any>) {
    return this.axios.request<Res>(config);
  }

  tokenIsExpired() {
    return new Date().getTime() > this.tokenExpireTime;
  }

  setTokenInterceptors() {
    this.axios.interceptors.request.use(
      async (config: any) => {
        if (!this.token) {
          return config;
        }

        let validToken = this.token;

        if (this.tokenIsExpired()) {
          validToken = await this.tokenRefresh();
        }

        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${validToken.access_token}`,
        };

        return config;
      },
      (err) => Promise.reject(err),
    );
  }

  async tokenRefresh() {
    const res = await axios.request<TokenResponse>({
      method: 'post',
      url: `${API_TOOLS_URL}/oauth2/refresh`,
      data: {},
      withCredentials: true,
    });

    const token = res.data.response;

    this.setToken(token);

    return token;
  }

  setToken(token: Token) {
    this.token = token;
    this.tokenExpireTime =
      new Date().getTime() + this.token.expires_in * 1000 - this.tokenExpireDelta * 1000;
  }

  async exchange(code: string) {
    await axios.request<TokenResponse>({
      method: 'post',
      url: `${API_TOOLS_URL}/oauth2/code/exchange`,
      data: {
        code,
        redirect_uri: AUTH_REDIRECT_URL,
      },
      withCredentials: true,
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
    });

    return this.tokenRefresh();
  }

  async logout() {
    await this.axios.request({
      method: 'post',
      url: `${API_TOOLS_URL}/oauth2/revoke`,
      data: {},
    });
    this.token = null;
  }

  async getOwnUserProfile() {
    const res = await this.axios.request<MyProfileResponse>({
      method: 'get',
      url: '/users/show/me.json',
    });

    return res.data;
  }
}
